import { Box, Heading, Paragraph, Popover, Table, TextButton } from '@zillow/constellation-10';
import { IconMessageFilled, IconPhoneFilled } from '@zillow/constellation-icons';
import { format } from 'date-fns';
import Link from 'next/link';
import { ModalType } from '../../../../types/ModalType';
import TriggerText from '../../../TriggerText';
import type { HandleOpenModal } from '../TableContent';
import { NOT_PROVIDED } from '../TableContent';
import {
  emitMessageEntryClickEvent,
  MESSAGE_ENTRY_CLICK_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/multiFamilyLeadsPage';
import { useAuth } from '../../../../AuthProvider';

type Props = {
  isPhoneLead: boolean;
  latestMessage: string;
  messageDateMs: number | undefined;
  onMessageClick: HandleOpenModal;
  senderName: string;
  listingAlias: string;
  conversationId: string;
};

export default function LatestContactTableCell({
  isPhoneLead,
  latestMessage,
  messageDateMs,
  onMessageClick,
  senderName,
  listingAlias,
  conversationId,
}: Props) {
  const { user } = useAuth();
  const timestamp = messageDateMs ? format(new Date(messageDateMs), 'M/d/yy h:mm a') : '';
  const popoverTimestamp = messageDateMs ? format(new Date(messageDateMs), 'yyyy MMM d h:mm a') : '';

  const handleOnMessageClick = () => {
    emitMessageEntryClickEvent(user);

    return onMessageClick(ModalType.SendMessageModal);
  };

  const voiceMessagePopoverContent = (
    <>
      <audio controls={true} style={{ height: '32px', width: '100%' }}>
        <source src={latestMessage} />
      </audio>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', marginTop: 'tighter' }}>
        {popoverTimestamp}
      </Paragraph>
      <Box css={{ paddingTop: 'default' }}>
        <TextButton asChild disabled={!listingAlias || !conversationId}>
          <Link
            href={{
              pathname: '/inbox/[[...conversationSlug]]',
              query: {
                conversationSlug: [listingAlias, conversationId],
                byProperty: 'true',
              },
            }}
          >
            View in messages
          </Link>
        </TextButton>
      </Box>
    </>
  );

  return (
    <Table.Cell css={{ verticalAlign: 'top' }}>
      {latestMessage ? (
        <>
          {isPhoneLead ? (
            <Popover
              body={voiceMessagePopoverContent}
              dividers
              header={<Heading level={4}>{`${senderName} latest contact`}</Heading>}
              shouldAwaitInteractionResponse
              tone="neutral"
              trigger={<TriggerText icon={<IconPhoneFilled />}>Voice</TriggerText>}
              placement="bottom"
            />
          ) : (
            <TriggerText
              data-eventid={MESSAGE_ENTRY_CLICK_EVENT_TYPE_ID}
              icon={<IconMessageFilled />}
              onClick={handleOnMessageClick}
            >
              Message
            </TriggerText>
          )}

          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginTop: 'tighter', whiteSpace: 'nowrap' }}>
            {timestamp}
          </Paragraph>
        </>
      ) : (
        <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
          {NOT_PROVIDED}
        </Paragraph>
      )}
    </Table.Cell>
  );
}
